import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useGetUltraSoundImageQuery } from "../../patient-dashboard/services/GetUltraSoundImagesAPISlice";
import { setUltrasoundImages } from "../../patient-dashboard/services/GetUltrasoundImagesDataSlice";
import { Box, Typography, useMediaQuery } from "@mui/material";
import DatewiseUltrasoundGroup from "./DatewiseUltrasoundGroup";
import LatestUltrasoundGroup from "./LatestUltrasoundGroup";
import { useTranslation } from "react-i18next";
import styles from "./MyUltrasound.module.scss";
import NoUltrasoundComponent from "../../patient-dashboard/widgets/ultrasound/NoUltrasoundComponent";
import DataLoader from "../../../components/custom-data-loader/DataLoader";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import EmptyComponent from "../../../components/v2/custom-empty-error-component/EmptyComponent";
import MyUltrasoundsSkeleton from "./MyUltrasoundsSkeleton";

const MyUltrasound: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ultrasoundData = useAppSelector((state) => state.patientUltraSoundData);

  const shouldFetch = ultrasoundData?.ultrasoundImages?.length === 0;
  const {
    data: ultrasoundRespone,
    isLoading,
    error: apiError,
  } = useGetUltraSoundImageQuery(undefined, {
    skip: !shouldFetch,
  });

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  // Store fetched data in Redux when available
  useEffect(() => {
    if (ultrasoundRespone && shouldFetch) {
      dispatch(setUltrasoundImages(ultrasoundRespone?.ultrasoundImages));
    }
  }, [ultrasoundRespone, shouldFetch, dispatch]);

  const [latestGroup, ...restGroups] = ultrasoundData?.ultrasoundImages;

  const bgProps = {
    variant: "default" as const,
    height: { xs: "600px", sm: "640px", md: "500px" },
    showFooter: true,
    backgroundColor: "#f2f0ec",
    backgroundFooterColor: "#f2f0ec",
  };

  return (
    <DataLoader
      isLoading={isLoading}
      error={apiError}
      data={ultrasoundData?.ultrasoundImages}
      loadingComponent={<MyUltrasoundsSkeleton/>}
      emptyComponent={<NoUltrasoundComponent />}
      errorComponent={
       <EmptyComponent message={t("ultrasoundPageError")} textColor="error"/>
      }
    >
      {() => (
        <Box>
          <BackgroundLayout {...bgProps}>
            <Box className={styles.root}>
              <Typography variant={isMobile ? "h3" : "h1"}>
                {t("myUltrasounds")}
              </Typography>
              <Typography className={styles.bodyText} >
                {t("myUltrasoundsSubText")}
              </Typography>

              <LatestUltrasoundGroup
                imageDate={latestGroup?.imageDate}
                imageCount={latestGroup?.imageCount}
                ultrasoundImages={latestGroup?.ultrasoundImages}
                preferredLanguage={patientPreferredLang}
              />
              <Box
                className={`${styles.groupContainer} ${
                  isMobile ? styles.mobile : ""
                }`}
              >
                {restGroups?.map((group, idx) => (
                  <DatewiseUltrasoundGroup
                    key={idx}
                    imageDate={group.imageDate}
                    imageCount={group.imageCount}
                    ultrasoundImages={group.ultrasoundImages}
                    preferredLanguage={patientPreferredLang}
                  />
                ))}
              </Box>
            </Box>
          </BackgroundLayout>
        </Box>
      )}
    </DataLoader>
  );
};

export default MyUltrasound;

